import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, Download, Calendar } from "lucide-react";

const PRESETS = [
  { label: "This Month", getRange: () => { const d = new Date(); return { from: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`, to: istToday() }; }},
  { label: "Last Month", getRange: () => { const d = new Date(); d.setMonth(d.getMonth()-1); const y=d.getFullYear(),m=String(d.getMonth()+1).padStart(2,'0'); return { from: `${y}-${m}-01`, to: `${y}-${m}-${new Date(y,d.getMonth()+1,0).getDate()}` }; }},
  { label: "This Quarter", getRange: () => { const d = new Date(); const q = Math.floor(d.getMonth()/3)*3; return { from: `${d.getFullYear()}-${String(q+1).padStart(2,'0')}-01`, to: istToday() }; }},
];

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState("");
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [dateTo, setDateTo] = useState(() => istToday());
  // FOLLOW-UP 1 (F1c): one filter, shared with the PnL Audit screen and the API.
  const [grnOnly, setGrnOnly] = useState(false);
  // FIX-UP N1b — the two filters the SKU Sales report takes
  const [dateBasis, setDateBasis] = useState("order_date");
  const [includeCancelled, setIncludeCancelled] = useState(false);

  useEffect(() => {
    api.get("/reports/list").then(r => setReports(r.data?.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const generate = async (reportId, reportName) => {
    setGenerating(reportId);
    try {
      const response = await api.get(`/reports/generate/${reportId}`, {
        params: { date_from: dateFrom, date_to: dateTo, grn_only: grnOnly ? 1 : 0,
                  date_basis: dateBasis, include_cancelled: includeCancelled ? 1 : 0 },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `ORBIT2_${reportId}_${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${reportName} downloaded`);
    } catch (err) { toast.error("Failed to generate report"); }
    finally { setGenerating(""); }
  };

  const groups = {};
  reports.forEach(r => { if (!groups[r.group]) groups[r.group] = []; groups[r.group].push(r); });

  return (
    <div data-testid="reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Reports Centre</h1>
          <p className="text-sm text-zinc-500 mt-1">Generate and download reports. All data is live.</p>
        </div>
        <div className="flex items-center gap-2">
          {PRESETS.map(p => (
            <Button key={p.label} variant="outline" className="rounded-sm h-7 text-[10px]" onClick={() => { const r = p.getRange(); setDateFrom(r.from); setDateTo(r.to); }}>{p.label}</Button>
          ))}
          <div className="flex items-center gap-1 ml-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs rounded-sm w-32" />
            <span className="text-zinc-400 text-xs">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs rounded-sm w-32" />
          </div>
          <label className="flex items-center gap-1 text-[10px] text-zinc-600 ml-2 cursor-pointer">
            <input type="checkbox" checked={grnOnly} onChange={e => setGrnOnly(e.target.checked)} data-testid="reports-grn-only" />
            Show GRN&apos;d only
          </label>
          <span className="text-[9px] text-zinc-400">(P&amp;L Summary and SKU Profitability only)</span>
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-zinc-200">
            <label className="flex items-center gap-1 text-[10px] text-zinc-600 cursor-pointer">
              Date basis
              <select value={dateBasis} onChange={e => setDateBasis(e.target.value)}
                      className="h-7 text-[10px] rounded-sm border border-zinc-200 px-1" data-testid="reports-date-basis">
                <option value="order_date">Order date</option>
                <option value="invoice_date">Invoice date</option>
              </select>
            </label>
            <label className="flex items-center gap-1 text-[10px] text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} data-testid="reports-include-cancelled" />
              Include cancelled &amp; rejected
            </label>
            <span className="text-[9px] text-zinc-400">(SKU Sales only)</span>
          </div>
        </div>
      </div>

      {loading ? <p className="text-xs text-zinc-400 text-center py-12">Loading reports...</p> : (
        <div className="space-y-6">
          {Object.entries(groups).map(([group, groupReports]) => (
            <div key={group}>
              <h2 className="font-heading text-lg font-semibold text-zinc-950 mb-3">{group} Reports</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupReports.map(r => (
                  <div key={r.id} className="border border-zinc-200 p-4 flex flex-col justify-between" data-testid={`report-${r.id}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FileSpreadsheet size={14} className="text-zinc-400" />
                        <h3 className="text-sm font-semibold text-zinc-900">{r.name}</h3>
                      </div>
                      <p className="text-[10px] text-zinc-500 mb-3">{r.description}</p>
                    </div>
                    <Button onClick={() => generate(r.id, r.name)} disabled={generating === r.id} variant="outline" className="rounded-sm h-7 text-xs w-full" data-testid={`generate-${r.id}`}>
                      {generating === r.id ? "Generating..." : <><Download size={12} /> Download Excel</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
